/**
 * HYDRATION — what every agent actually reads when it wakes up.
 *
 * Erez, 2026-08-09: "show me all the files in boxes — which files the agent
 * reads at hydration, based on what the CLAUDE.md is saying. I want to
 * visualise it."
 *
 * So this is not a picture of what the doctrine SAYS should happen. It is a
 * picture of what the fifteen `agents/<name>/CLAUDE.md` files literally
 * instruct, read off disk on 9 August 2026, plus the sizes of the rows those
 * instructions resolve to in Neon.
 *
 * The layout carries one argument, left to right:
 *
 *   WHO  →  the two local files that are the whole contract
 *        →  the FILTER each contract applies
 *        →  the rows that come back
 *        →  and, unwired, the five documents no contract names at all.
 *
 * Column five is the finding. Every box in it exists, is current, is good, and
 * is loaded by nobody — `tailormind-platform` even carries the line "every
 * agent reads it, every agent keeps it true" in its own subtitle. Grepping all
 * fifteen contracts for it returns zero. An orphan box with no wire into it is
 * the most honest way to draw that.
 *
 * The red wire is the second half of the same story: a builder's project read
 * ends in `WHERE owner_agent = <self>`, so it returns one row out of fifteen.
 * Memory is organised by AGENT; the work is organised by SYSTEM. That mismatch
 * is why platform knowledge only ever accumulated in Atlas.
 *
 * Data object on the house engine (`./c4`) — nothing has a hand-written width,
 * and every wire resolves against the placed rects. Wires only ever join
 * ADJACENT columns: `c4` paints the wire SVG first and the boxes over it, so a
 * long wire spanning a column would be buried under that column's fill
 * (skill://diagrams §7).
 */
import { Canvas, leaf, person, box, type Diagram } from "./c4";

const HYDRATION: Diagram = (() => {
  // Bare actors, C4 style — the three reading classes. They differ only in
  // which rows come back, which is exactly what the wires show.
  const who = box("hy-who", undefined, "col", [
    person("hy-jarvis", "Jarvis · chief of staff"),
    person("hy-atlas", "Atlas · R&D"),
    person("hy-build", "5 builders"),
  ], { pad: 0, gap: 30 });

  // The ONLY local files hydration touches. Everything else is a query.
  const contract = box("hy-ctr", "THE CONTRACT — the only local files", "col", [
    leaf("hy-root", "CLAUDE.md", "[workspace root · shared]", "FileCode"),
    leaf("hy-own-md", "agents/<name>/CLAUDE.md", "[identity + the diet]", "FileCode"),
  ], { gap: 12 });

  // The clause each contract actually writes. Two of the three are self-scoped,
  // and that is the whole mechanism — nothing is hidden, it is just filtered.
  const filter = box("hy-flt", "WHAT THE CONTRACT ASKS FOR", "col", [
    leaf("hy-slug", "page_slug IN (…)", "[an explicit list]", "ListFilter"),
    leaf("hy-agentf", "WHERE agent = self", "[last 20 logs]", "Filter"),
    leaf("hy-ownf", "WHERE owner_agent = self", "[the wall]", "ShieldAlert", "gate"),
  ], { gap: 12 });

  // What comes back. Split by SCOPE, because scope is the subject.
  //
  // Each group stacks its leaves in a COLUMN rather than a row, and that is a
  // wire-legibility decision as much as a visual one. `place()` centres every
  // child on the cross-axis with no way to stretch (the Box `align` field is
  // declared but never read), so a one-leaf group laid out as a row ends up far
  // narrower than a two-leaf one and its left edge sits deep inside the parent.
  // Measured on the first render: the container's left edge was 896 and the
  // one-leaf Shared group started at 997, which put that wire's midpoint — and
  // so its label and disc — 21px UNDER the container fill. Stacking makes all
  // three groups roughly equal width, so every midpoint lands in the open gap.
  const brain = box("hy-brain", "WHAT COMES BACK — Neon", "col", [
    box("hy-shared", "Shared — every agent, same rows", "col", [
      leaf("hy-learn", "Company Learnings", "[kb-doc/company/learnings · 106K]", "Brain"),
    ], { depth: 2, gap: 10, pad: 10 }),
    box("hy-self", "Per agent — self only", "col", [
      leaf("hy-sess", "Session Summary", "[kb-doc/agents/<self>/… · ~30K cap]", "FileText"),
      leaf("hy-diary", "Diary", "[memories · last 20]", "ScrollText"),
    ], { depth: 2, gap: 10, pad: 10 }),
    box("hy-projs", "Per project — owner only", "col", [
      leaf("hy-proj", "Project Summary", "[projects · 1 of 15 rows]", "FolderOpen"),
      leaf("hy-port", "Portfolio", "[all 15 project rows · Jarvis only]", "LayoutGrid"),
    ], { depth: 2, gap: 10, pad: 10 }),
  ], { gap: 12 });

  // Column five. Nothing points at the top box, and that is the content.
  const unread = box("hy-not", "NOT LOADED ON ANY HYDRATION", "col", [
    box("hy-orphan", "Orphaned — 0 of 15 contracts name these", "col", [
      leaf("hy-plat", "Tailormind platform", "[myjarvis-os/architecture · 11.6K]", "Layers", "gate"),
      leaf("hy-std", "The Tailormind standard", "[consultancy/falcon · 9.8K]", "BookOpen", "gate"),
      leaf("hy-vs", "Falcon vs Boiling", "[what repeated · 10.4K]", "GitCompare", "gate"),
      leaf("hy-omni", "Falcon on omnigent", "[consultancy/falcon · 36K]", "Boxes", "gate"),
      leaf("hy-vend", "omnigent architecture", "[the vendor layer · 5.8K]", "Package", "gate"),
    ], { depth: 2, gap: 10, pad: 10 }),
    box("hy-later", "On demand — fetched mid-run, never at wake", "col", [
      leaf("hy-skill", "skill://…", "[dashboard · board · diagrams]", "ListChecks"),
      leaf("hy-index", "KB folder index", "[IndexFirst hook · every prompt]", "FolderTree"),
    ], { depth: 2, gap: 10, pad: 10 }),
  ], { gap: 12 });

  return {
    key: "hy",
    title: "Hydration — what an agent reads when it wakes",
    subtitle: "Read off the 15 CLAUDE.md files, not off the doctrine · five documents no contract names · v1.0 · 2026-08-09",
    padBottom: 92,
    cols: [
      { root: who, align: "center" },
      { root: contract, align: "center" },
      { root: filter, align: "center" },
      { root: brain, align: "center" },
      { root: unread, align: "center" },
    ],
    // TWO rules, both learned by reading the render rather than the source.
    //
    // 1 · Labels stay ~4 characters. COL_GAP is 58px and `c4` paints the wire
    //     SVG UNDER the boxes, so anything wider runs beneath a container and
    //     is clipped mid-word (skill://diagrams §7). The discs carry the
    //     sequence, the legend carries the sentence.
    // 2 · Anchor on the CONTAINER edge, never on a leaf inside it. A wire from
    //     an inner leaf starts behind its own container's fill, which drags the
    //     midpoint — and therefore the label and the disc — under the far
    //     container. Anchoring both ends on outer edges puts every midpoint in
    //     the true gap, where it is visible.
    wires: [
      { a: "hy-who", b: "hy-ctr", as: "r", bs: "l", label: "folder", num: 1 },
      { a: "hy-ctr", b: "hy-flt", as: "r", bs: "l", label: "asks", num: 2 },
      { a: "hy-flt", b: "hy-shared", as: "r", bs: "l", label: "all", num: 3 },
      { a: "hy-flt", b: "hy-self", as: "r", bs: "l", label: "self", num: 4 },
      { a: "hy-flt", b: "hy-projs", as: "r", bs: "l", label: "1/15", num: 5, red: true },
    ],
    legend: [
      { style: "solid", label: "1 the launch folder is the identity · 2 the contract names what to ask for · 3–4 the rows that come back" },
      { style: "red", label: "5 the wall — WHERE owner_agent = self returns one project row out of fifteen" },
    ],
  };
})();

/** What an agent actually loads when it wakes, and the documents it never does. */
export function HydrationDiagram() {
  return <Canvas d={HYDRATION} mode="contrast" />;
}
